import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical, Eye } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { householdCompounds, householdCompoundColors } from '@/config/experiments/chem1131/experiment1.config';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Selecting Insoluble Compounds',
    content: `After the solubility test, we have 3 insoluble compounds to further identify:

**The Three Insoluble Compounds:**
1. **CaCO₃** (Calcium carbonate) - Chalk
2. **CaSO₄** (Calcium sulfate) - Plaster of Paris
3. **Corn Starch** - Polysaccharide from corn

**Why Focus on Insolubles?**
The soluble compounds require different tests (phenolphthalein, vinegar). For insolubles, the iodine test efficiently identifies starch.

**Identification Strategy:**
- Iodine test → Identifies corn starch
- Vinegar test → Distinguishes CaCO₃ from CaSO₄`
  },
  2: {
    title: 'The Iodine-Starch Reaction',
    content: `The iodine test is a classic qualitative test for starch, producing a distinctive blue-black color.

**The Chemistry:**
Starch consists of two components:
- **Amylose** (20-30%): Linear helical chains
- **Amylopectin** (70-80%): Branched structure

Iodine molecules (I₂) slip inside the amylose helix, forming a **charge-transfer complex** that absorbs light differently, appearing blue-black.

**Why Only Starch?**
- Amylose's helical structure perfectly fits I₂ molecules
- Other carbohydrates lack this coiled structure
- Simple sugars (glucose, sucrose) give no color change

**Concentration Matters:**
Just 1-2 drops of iodine tincture is sufficient. Too much obscures the color.`
  },
  3: {
    title: 'Interpreting Iodine Test Results',
    content: `The color change is dramatic and unmistakable when starch is present.

**Result Interpretation:**
| Color | Result | Compound |
|-------|--------|----------|
| Blue-black | POSITIVE | Corn starch confirmed |
| Brown-purple | Negative | Not starch (CaCO₃ or CaSO₄) |

**Why Brown-Purple for Negatives?**
The iodine solution itself is brownish. Without starch to form a complex, you simply see diluted iodine color.

**Recording Results:**
✓ Corn starch is now positively identified
✓ CaCO₃ and CaSO₄ remain to be distinguished
→ Proceed to vinegar test (Part IV)`
  }
};

type IodineResult = 'none' | 'negative' | 'positive';

export default function Part3Iodine() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [insolubleSelected, setInsolubleSelected] = useState(false);
  const [iodineAdded, setIodineAdded] = useState(false);
  const [resultsRecorded, setResultsRecorded] = useState(false);
  const [iodineResults, setIodineResults] = useState<Record<string, IodineResult>>({});
  const [currentStep, setCurrentStep] = useState(1);

  // Only the insoluble compounds
  const insolubleCompounds = [
    householdCompounds.caco3,
    householdCompounds.caso4,
    householdCompounds.cornstarch
  ];

  const completedSteps = [insolubleSelected, iodineAdded, resultsRecorded].filter(Boolean).length;

  const handleSelectInsoluble = useCallback(() => {
    if (!insolubleSelected) {
      setInsolubleSelected(true);
      setCurrentStep(2);
    }
  }, [insolubleSelected]);

  const handleAddIodine = useCallback((compoundId: string) => {
    if (insolubleSelected && !iodineAdded) {
      setIodineResults(prev => {
        const compound = householdCompounds[compoundId];
        const result: IodineResult = compound.iodinePositive ? 'positive' : 'negative';
        const updated = { ...prev, [compoundId]: result };

        // Check if all tested
        if (Object.keys(updated).length === insolubleCompounds.length) {
          setIodineAdded(true);
          setCurrentStep(3);
        }
        return updated;
      });
    }
  }, [insolubleSelected, iodineAdded, insolubleCompounds.length]);

  const handleRecordResults = useCallback(() => {
    if (iodineAdded && !resultsRecorded) {
      setResultsRecorded(true);
    }
  }, [iodineAdded, resultsRecorded]);

  const getIodineColor = (compoundId: string, result: IodineResult) => {
    if (result === 'none') return 'transparent';
    const compound = householdCompounds[compoundId];
    return compound.iodinePositive ? householdCompoundColors.iodineStarch : householdCompoundColors.iodineNegative;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part III: Iodine Test</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Select Insoluble', hint: 'Identify the 3 insoluble compounds', done: insolubleSelected },
            { id: 2, title: 'Add Iodine', hint: 'Add 1-2 drops to each', done: iodineAdded },
            { id: 3, title: 'Record Results', hint: 'Note color changes', done: resultsRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Theory Panel */}
        <div className="theory-panel">
          <h4>Starch-Iodine Test</h4>
          <p>
            Iodine (I<sub>2</sub>) forms a deep blue-black complex with starch.
            The iodine molecules insert into the helical amylose structure.
          </p>
          <div className="color-key">
            <div className="key-item">
              <span className="color-swatch negative"></span>
              <span>Brownish-purple (Negative)</span>
            </div>
            <div className="key-item">
              <span className="color-swatch positive"></span>
              <span>Blue-black (POSITIVE - Starch)</span>
            </div>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Iodine Test for Starch"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Iodine tincture', 'Dropper', 'Watch glass']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="iodine-workspace">
          {/* Iodine Bottle */}
          <div className="reagent-area">
            <div className="iodine-bottle">
              <svg viewBox="0 0 50 90" width={60} height={100}>
                <rect x="12" y="30" width="26" height="50" rx="3" fill="#3A3A3A" />
                <rect x="15" y="35" width="20" height="40" fill="#6D4C41" opacity="0.8" />
                <rect x="17" y="15" width="16" height="18" rx="2" fill="#222" />
                <text x="25" y="60" fontSize="5" fill="#FFF" textAnchor="middle">I2</text>
                <text x="25" y="70" fontSize="4" fill="#DDD" textAnchor="middle">Tincture</text>
              </svg>
              <span className="bottle-label">Iodine Solution</span>
            </div>
          </div>

          {/* Insoluble Compounds Selection */}
          {!insolubleSelected && (
            <div className="selection-prompt" onClick={handleSelectInsoluble}>
              <Eye size={32} className="prompt-icon" />
              <h3>Identify Insoluble Compounds</h3>
              <p>From the solubility test, these 3 compounds did not dissolve:</p>
              <div className="compound-list">
                {insolubleCompounds.map(c => (
                  <span key={c.id} className="compound-chip">{c.formula} ({c.commonName})</span>
                ))}
              </div>
              <button className="select-btn">Proceed with Iodine Test</button>
            </div>
          )}

          {/* Test Tubes for Insoluble Compounds */}
          {insolubleSelected && (
            <div className="test-tubes-section">
              <div className="tubes-row">
                {insolubleCompounds.map((compound) => {
                  const result = iodineResults[compound.id] || 'none';
                  const isPositive = compound.iodinePositive;

                  return (
                    <div
                      key={compound.id}
                      className={`tube-station ${
                        currentStep === 2 && result === 'none' ? 'clickable' : ''
                      }`}
                      onClick={() => {
                        if (currentStep === 2 && result === 'none') {
                          handleAddIodine(compound.id);
                        }
                      }}
                    >
                      <svg viewBox="0 0 50 110" width={60} height={130}>
                        {/* Test tube */}
                        <rect x="10" y="10" width="30" height="75" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                        <ellipse cx="25" cy="85" rx="15" ry="6" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1.5" />

                        {/* Solid at bottom */}
                        <ellipse cx="25" cy="75" rx="12" ry="5" fill="#F5F5F5" opacity="0.9" />

                        {/* Iodine solution if added */}
                        {result !== 'none' && (
                          <g>
                            <rect x="12" y="40" width="26" height="35" fill={getIodineColor(compound.id, result)} rx="2" />
                            {/* Highlight for positive (starch) */}
                            {isPositive && (
                              <rect x="14" y="42" width="6" height="20" fill="rgba(255,255,255,0.1)" rx="1" />
                            )}
                          </g>
                        )}

                        {/* Label */}
                        <rect x="12" y="15" width="26" height="16" fill="#FFFACD" stroke="#DAA520" strokeWidth="0.5" rx="2" />
                        <text x="25" y="26" fontSize="7" fill="#333" textAnchor="middle" fontWeight="bold">
                          {compound.formula.substring(0, 6)}
                        </text>
                      </svg>

                      <span className="tube-name">{compound.commonName}</span>

                      {/* Result badge */}
                      {result !== 'none' && (
                        <span className={`result-badge ${result}`}>
                          {result === 'positive' ? 'STARCH' : 'Negative'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Dropper Animation Guide */}
              {currentStep === 2 && !iodineAdded && (
                <div className="dropper-guide">
                  <Droplets size={16} />
                  <span>Click each tube to add iodine drops ({Object.keys(iodineResults).length}/3)</span>
                </div>
              )}
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {iodineAdded && !resultsRecorded && (
              <div className="status-message info" onClick={handleRecordResults} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click to record results: Corn starch turned blue-black (POSITIVE)
              </div>
            )}
            {resultsRecorded && (
              <div className="status-message success">
                <Check size={20} />
                Iodine test complete! Corn starch identified by blue-black color.
              </div>
            )}
          </div>

          {/* Results Summary */}
          {resultsRecorded && (
            <div className="results-box">
              <h4>Iodine Test Results</h4>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Compound</th>
                    <th>Color</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {insolubleCompounds.map(compound => (
                    <tr key={compound.id} className={compound.iodinePositive ? 'positive' : ''}>
                      <td>{compound.formula}</td>
                      <td>{compound.iodinePositive ? 'Blue-black' : 'Brown-purple'}</td>
                      <td>{compound.iodinePositive ? 'POSITIVE (Starch)' : 'Negative'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="conclusion">
                Corn starch is now identified. CaCO<sub>3</sub> and CaSO<sub>4</sub> require additional tests.
              </p>
            </div>
          )}
        </div>

        <style>{`
          .iodine-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .reagent-area {
            display: flex;
            justify-content: center;
          }

          .iodine-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .bottle-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .selection-prompt {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 400px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .selection-prompt:hover {
            border-color: var(--secondary);
          }

          .prompt-icon {
            color: #F59E0B;
            margin-bottom: 12px;
          }

          .selection-prompt h3 {
            margin: 0 0 8px 0;
            color: var(--text-primary);
          }

          .selection-prompt p {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .compound-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            justify-content: center;
            margin-bottom: 16px;
          }

          .compound-chip {
            background: rgba(245, 158, 11, 0.2);
            color: #F59E0B;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 13px;
          }

          .select-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
          }

          .test-tubes-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }

          .tubes-row {
            display: flex;
            gap: 30px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 20px 30px;
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          }

          .tube-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .tube-station.clickable {
            cursor: pointer;
          }

          .tube-station.clickable:hover {
            background: rgba(255,255,255,0.1);
          }

          .tube-name {
            font-size: 11px;
            color: #CCC;
          }

          .result-badge {
            font-size: 10px;
            padding: 3px 8px;
            border-radius: 4px;
            font-weight: 600;
          }

          .result-badge.positive {
            background: #1A237E;
            color: white;
          }

          .result-badge.negative {
            background: #6D4C41;
            color: white;
          }

          .dropper-guide {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #60A5FA;
            font-size: 14px;
          }

          .theory-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .theory-panel h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .theory-panel p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .color-key {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .key-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: var(--text-secondary);
          }

          .color-swatch {
            width: 20px;
            height: 12px;
            border-radius: 2px;
          }

          .color-swatch.negative {
            background: #6D4C41;
          }

          .color-swatch.positive {
            background: #1A237E;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .results-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 450px;
            border-left: 3px solid var(--secondary);
          }

          .results-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .results-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 12px;
            font-size: 13px;
          }

          .results-table th, .results-table td {
            padding: 8px;
            text-align: left;
            border-bottom: 1px solid var(--border);
          }

          .results-table th {
            color: var(--text-secondary);
            font-weight: 600;
          }

          .results-table tr.positive {
            background: rgba(26, 35, 126, 0.2);
          }

          .results-table tr.positive td {
            color: #7986CB;
            font-weight: 500;
          }

          .conclusion {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            font-style: italic;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
